/*
 * lead-form-revi
 * Formulário de captação de leads que envia direto para a Revi.
 *
 * Contrato replicado do widget oficial da Revi (revi-popup.js):
 *   POST https://public-api.userevi.com/widgets/leads
 *   headers: public-site-key, request-url, request-pathname
 *   phoneNumber -> somente dígitos | birthDate -> AAAA-MM-DD
 */

const REVI_ENDPOINT = 'https://public-api.userevi.com';

const REVI_MESSAGES = {
  name: 'Informe seu nome.',
  email: 'E-mail inválido.',
  phone: 'Telefone inválido. Use DDD + número.',
  dateFormat: 'Use DD/MM/AAAA.',
  dateInvalid: 'Data inválida.',
  dateFuture: 'A data não pode ser no futuro.',
  consent: 'É preciso aceitar para continuar.',
  network: 'Não conseguimos enviar seus dados agora. Confira sua conexão e tente de novo.',
  server: 'Não conseguimos concluir seu cadastro agora. Tente de novo em instantes.',
};

function onlyDigits(value) {
  return (value || '').replace(/\D/g, '');
}

function maskPhoneBR(value) {
  const d = onlyDigits(value).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return '(' + d.slice(0, 2) + ') ' + d.slice(2);
  if (d.length <= 10) return '(' + d.slice(0, 2) + ') ' + d.slice(2, 6) + '-' + d.slice(6);
  return '(' + d.slice(0, 2) + ') ' + d.slice(2, 7) + '-' + d.slice(7);
}

function maskDateBR(value) {
  const d = onlyDigits(value).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return d.slice(0, 2) + '/' + d.slice(2);
  return d.slice(0, 2) + '/' + d.slice(2, 4) + '/' + d.slice(4);
}

// DD/MM/AAAA -> AAAA-MM-DD (sem timezone), igual ao widget da Revi
function normalizeDateBRtoISO(value) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || '');
  if (!m) return value || '';
  return m[3] + '-' + m[2] + '-' + m[1];
}

const Validators = {
  name(value) {
    return (value || '').trim().length >= 2 ? { ok: true } : { ok: false, msg: REVI_MESSAGES.name };
  },
  email(value) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    return re.test(value || '') ? { ok: true } : { ok: false, msg: REVI_MESSAGES.email };
  },
  phoneBR(value) {
    const d = onlyDigits(value);
    return d.length === 10 || d.length === 11 ? { ok: true } : { ok: false, msg: REVI_MESSAGES.phone };
  },
  dateBR(value) {
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value || '')) return { ok: false, msg: REVI_MESSAGES.dateFormat };
    const parts = value.split('/').map(Number);
    const dd = parts[0];
    const mm = parts[1];
    const yyyy = parts[2];
    const dt = new Date(yyyy, mm - 1, dd);
    const real = dt.getFullYear() === yyyy && dt.getMonth() === mm - 1 && dt.getDate() === dd;
    if (!real) return { ok: false, msg: REVI_MESSAGES.dateInvalid };
    if (dt > new Date()) return { ok: false, msg: REVI_MESSAGES.dateFuture };
    return { ok: true };
  },
};

class LeadFormRevi extends HTMLElement {
  constructor() {
    super();
    this.submitting = false;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.submitButton = this.querySelector('[data-lead-submit]');
    this.alertBox = this.querySelector('[data-lead-alert]');
    this.inputs = {
      name: this.querySelector('[name="name"]'),
      phoneNumber: this.querySelector('[name="phoneNumber"]'),
      email: this.querySelector('[name="email"]'),
      birthDate: this.querySelector('[name="birthDate"]'),
    };
    this.consent = this.querySelector('[name="consent"]');

    if (this.inputs.phoneNumber) {
      this.inputs.phoneNumber.addEventListener('input', (event) => {
        event.target.value = maskPhoneBR(event.target.value);
      });
    }

    if (this.inputs.birthDate) {
      this.inputs.birthDate.addEventListener('input', (event) => {
        event.target.value = maskDateBR(event.target.value);
      });
    }

    Object.values(this.inputs).forEach((input) => {
      if (input) input.addEventListener('input', () => this.clearFieldError(input));
    });

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.onSubmit();
    });
  }

  fieldWrapper(input) {
    return input.closest('.lead-form-revi__field');
  }

  clearFieldError(input) {
    const wrapper = this.fieldWrapper(input);
    if (!wrapper) return;
    wrapper.classList.remove('field--error');
    input.removeAttribute('aria-invalid');
  }

  setFieldError(input, message) {
    const wrapper = this.fieldWrapper(input);
    if (!wrapper) return;
    wrapper.classList.add('field--error');
    input.setAttribute('aria-invalid', 'true');
    const errorEl = wrapper.querySelector('.lead-form-revi__field-error');
    if (errorEl) errorEl.textContent = message;
  }

  showAlert(message) {
    if (!this.alertBox) return;
    this.alertBox.textContent = message;
    this.alertBox.classList.add('is-visible');
  }

  hideAlert() {
    if (!this.alertBox) return;
    this.alertBox.textContent = '';
    this.alertBox.classList.remove('is-visible');
  }

  validate() {
    const checks = [
      [this.inputs.name, Validators.name, true],
      [this.inputs.phoneNumber, Validators.phoneBR, true],
      [this.inputs.email, Validators.email, true],
      [this.inputs.birthDate, Validators.dateBR, false],
    ];

    let firstInvalid = null;

    checks.forEach((check) => {
      const input = check[0];
      const validator = check[1];
      const required = check[2];
      if (!input) return;
      const value = input.value.trim();
      if (!required && value === '') {
        this.clearFieldError(input);
        return;
      }
      const result = validator(value);
      if (result.ok) {
        this.clearFieldError(input);
      } else {
        this.setFieldError(input, result.msg);
        if (!firstInvalid) firstInvalid = input;
      }
    });

    if (firstInvalid) {
      firstInvalid.focus();
      return false;
    }

    const consentRequired = this.dataset.consentRequired !== 'false';
    if (consentRequired && this.consent && !this.consent.checked) {
      this.showAlert(REVI_MESSAGES.consent);
      this.consent.focus();
      return false;
    }

    return true;
  }

  buildPayload() {
    const payload = {
      name: this.inputs.name.value.trim(),
      email: this.inputs.email.value.trim(),
      phoneNumber: onlyDigits(this.inputs.phoneNumber.value),
      isOptedInToNewsletter: this.consent ? this.consent.checked : true,
    };

    const birthDate = this.inputs.birthDate ? this.inputs.birthDate.value.trim() : '';
    if (birthDate) payload.birthDate = normalizeDateBRtoISO(birthDate);

    const sourceId = this.dataset.sourceId;
    if (sourceId) payload.sourceId = sourceId;

    return payload;
  }

  setBusy(busy) {
    this.submitting = busy;
    if (!this.submitButton) return;
    this.submitButton.setAttribute('aria-disabled', busy ? 'true' : 'false');
    this.submitButton.disabled = busy;
  }

  async onSubmit() {
    if (this.submitting) return;
    this.hideAlert();
    if (!this.validate()) return;

    const siteKey = this.dataset.siteKey;
    if (!siteKey) {
      this.showAlert(REVI_MESSAGES.network);
      console.error('[lead-form-revi] data-site-key ausente na section.');
      return;
    }

    this.setBusy(true);

    try {
      // A Revi resolve o lead contra um pop-up configurado para esta URL.
      // Se a página não tiver pop-up ativo na Revi, o endpoint devolve 400
      // antes mesmo de validar o corpo.
      const response = await fetch(REVI_ENDPOINT + '/widgets/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'public-site-key': siteKey,
          'request-url': window.location.href,
          'request-pathname': window.location.pathname,
        },
        body: JSON.stringify(this.buildPayload()),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.error('[lead-form-revi] Revi respondeu HTTP ' + response.status + ':', text);
        this.showAlert(REVI_MESSAGES.server);
        this.setBusy(false);
        return;
      }

      this.onSuccess();
    } catch (error) {
      // Só cai aqui se a requisição nem chegou a completar (rede, DNS, CORS).
      console.error('[lead-form-revi] falha de rede:', error);
      this.showAlert(REVI_MESSAGES.network);
      this.setBusy(false);
    }
  }

  onSuccess() {
    this.setBusy(false);
    this.dataset.state = 'success';

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', {
        method: 'revi',
        form_id: this.id || 'lead-form-revi',
        send_to: 'G-YFLTWWFDJW',
      });
    }

    const success = this.querySelector('[data-lead-success]');
    if (success) {
      success.setAttribute('tabindex', '-1');
      success.focus();
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const redirect = this.dataset.successRedirect;
    if (redirect) {
      setTimeout(() => {
        window.location.href = redirect;
      }, 2000);
    }
  }
}

if (!customElements.get('lead-form-revi')) {
  customElements.define('lead-form-revi', LeadFormRevi);
}
